import {
  setBenefitStructurePreference,
  setDeathBenefitPreference,
  setPaymentStructurePreference,
} from '@/model/ltcFinancialPlanningModel';
import { useToast } from '@client/components/hooks/use-toast';
import {
  PreferenceOption,
  PreferencePagination,
  PreferenceQuestion,
} from '@client/components/ltc-financial-planning/PreferenceQuestion';
import { AppState } from '@client/model/store';
import { Calendar, Heart, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BottomNavigation from '@/components/ltc-financial-planning/BottomNavigation';
import { StepContentProps } from '../types';

const DEATH_BENEFIT_OPTIONS: PreferenceOption<LtcFinancialPlanningDeathBenefitPreference>[] = [
  {
    value: 'yes',
    title: 'Yes, That Matters to Me',
    description: 'I want to leave something behind',
  },
  {
    value: 'no',
    title: 'No, Not a Priority',
    description: 'Focus on my care needs instead',
  },
];

const PAYMENT_STRUCTURE_OPTIONS: PreferenceOption<LtcFinancialPlanningPaymentStructure>[] = [
  {
    value: 'upfront',
    title: 'Pay Everything Upfront',
    description: 'One-time lump sum payment',
  },
  {
    value: 'monthly',
    title: 'Small Monthly Premiums',
    description: 'Lower payments spread over time',
  },
  {
    value: 'balanced',
    title: 'Somewhere in the Middle',
    description: 'Balanced payment approach',
  },
  {
    value: 'noPreference',
    title: "I Don't Have a Preference",
    description: 'Show me all options',
  },
];

const BENEFIT_STRUCTURE_OPTIONS: PreferenceOption<LtcFinancialPlanningBenefitStructure>[] = [
  {
    value: 'maxValue',
    title: 'Most Bang for Your Buck',
    description: 'Higher benefits, but use-it-or-lose-it',
  },
  {
    value: 'guaranteed',
    title: 'Guaranteed Benefits',
    description: 'Always get something back, even if unused',
  },
];

export function PreferencesStep({ navigation }: StepContentProps) {
  const dispatch = useDispatch();
  const { toast, dismiss } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [toastId, setToastId] = useState<string | null>(null);

  const { preferences } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  const isAnswered = isCurrentQuestionAnswered();

  // Dismiss toast when question is answered
  useEffect(() => {
    if (isAnswered && toastId) {
      dismiss(toastId);
      setToastId(null);
    }
  }, [isAnswered, dismiss, toastId]);

  // Check if current question is answered
  function isCurrentQuestionAnswered() {
    switch (currentQuestion) {
      case 1:
        return preferences.deathBenefit !== null;
      case 2:
        return preferences.paymentStructure !== null;
      case 3:
        return preferences.benefitStructure !== null;
      default:
        return false;
    }
  }

  const handleDeathBenefitSelect = (value: LtcFinancialPlanningDeathBenefitPreference) => {
    dispatch(setDeathBenefitPreference(value));
  };

  const handlePaymentStructureSelect = (value: LtcFinancialPlanningPaymentStructure) => {
    dispatch(setPaymentStructurePreference(value));
  };

  const handleBenefitStructureSelect = (value: LtcFinancialPlanningBenefitStructure) => {
    dispatch(setBenefitStructurePreference(value));
  };

  // Handle Continue - go to next question or next step
  const handleContinue = () => {
    if (!isAnswered) {
      const result = toast({
        title: 'Action Required',
        description: 'Please answer this question to continue',
        variant: 'default',
        className: 'border-yellow-500 bg-yellow-50 text-yellow-800',
      });
      setToastId(result.id);
      return;
    }

    if (currentQuestion < 3) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // On last question, go to next step
      navigation.goNext();
    }
  };

  // Handle Back - go to previous question or previous step
  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      // On first question, go to previous step
      navigation.goPrev();
    }
  };

  return (
    <div className="py-4">
      {/* Question Counter */}
      <p className="text-primary mb-6 text-center text-sm font-medium">
        Question {currentQuestion} of 3
      </p>

      {/* Question 1: Death Benefits */}
      {currentQuestion === 1 && (
        <PreferenceQuestion
          icon={<Heart className="h-7 w-7" />}
          title="Do you care about death benefits?"
          description="Would leaving a legacy to your children or loved ones be important to you?"
          options={DEATH_BENEFIT_OPTIONS}
          selectedValue={preferences.deathBenefit}
          onSelect={handleDeathBenefitSelect}
        />
      )}

      {/* Question 2: Payment Structure */}
      {currentQuestion === 2 && (
        <PreferenceQuestion
          icon={<Calendar className="h-7 w-7" />}
          title="How do you prefer to pay?"
          description="Choose the payment structure that works best for your financial situation."
          options={PAYMENT_STRUCTURE_OPTIONS}
          selectedValue={preferences.paymentStructure}
          onSelect={handlePaymentStructureSelect}
        />
      )}

      {/* Question 3: Benefit Structure */}
      {currentQuestion === 3 && (
        <>
          <PreferenceQuestion
            icon={<Shield className="h-7 w-7" />}
            title="What type of benefit structure?"
            description="Choose between maximum value or guaranteed returns."
            options={BENEFIT_STRUCTURE_OPTIONS}
            selectedValue={preferences.benefitStructure}
            onSelect={handleBenefitStructureSelect}
          />
        </>
      )}

      {/* Pagination Dots */}
      <PreferencePagination
        currentQuestion={currentQuestion}
        totalQuestions={3}
        className="mt-8"
      />

      <BottomNavigation
        navigation={navigation}
        onContinue={handleContinue}
        canContinue={isAnswered}
        onBack={handleBack}
      />
    </div>
  );
}
